<template>
  <component :is="wrapperTag" :class="wrapperClass()">
    <component
      :is="labelTag"
      v-if="label"
      :id="field.horizontal ? labelId : undefined"
      :class="[labelClass(), labelClasses, 'margin-bottom-10', field.horizontal ? '' : 'radio-group-legend', headerCompactClass]"
    >
      <span v-if="field.required" class="required">*</span>
      <span :class="labelClasses" v-html="label"></span>
    </component>
    <div v-if="field.description" class="radio-field-description">{{ field.description }}</div>
    <div v-if="field.disclaimer" class="radio-field-disclaimer">{{ field.disclaimer }}</div>
    <input type="hidden" :name="name" :id="name + 'notselected'" value="" />
    <div
      class="btn-group btn-group-toggle row gx-2 gy-2"
      data-toggle="buttons"
      :class="selectionClass()"
      :role="field.horizontal ? 'radiogroup' : undefined"
      :aria-labelledby="field.horizontal ? labelId : undefined"
    >
      <div v-for="(option,idx) in options" :key="idx" :class="innerClass(this.options.length)">
        <label class="">
          <input class="red-radio" type="radio" :name="name" :required="field.required" :aria-required="fieldAriaRequired" :aria-describedby="fieldDescribedBy" :aria-invalid="fieldAriaInvalid" autocomplete="off" :value="option.val" :checked="option.val==value" v-model="selected"> <span v-if="hasOptionDetail(option)" class="radio-option-detail"><span v-if="option.title" class="radio-option-title">{{ option.title }}</span><span v-else v-html="option.text"></span><span v-if="option.description" class="radio-option-description">{{ option.description }}</span></span><span v-else v-html="option.text"></span>
        </label>
      </div>
    </div>
    <!-- Move nested fields outside the option loop so they can span full width -->
    <div v-if="selectedOptionWithFields" v-show="this.selected==selectedOptionWithFields.val" :class="nestedFieldsWrapperClass()">
      <div v-for="(field,idx) in selectedOptionWithFields.fields" :key="idx" :class="nestedFieldColumnClass()">
        <FormFieldComponent
          :field="customizeNestedField(field)"
        />
      </div>
    </div>
    <div class="invalid-feedback" :id="`${name}invalid`">{{field.error_message}}</div>
  </component>
</template>
<script>
import formFieldA11y from '@/mixins/formFieldA11y.js'

export default {
  mixins: [formFieldA11y],
  props: {
    label: String,
    type: String,
    name: String,
    value: {
      type: [Number, String],
      default: null,
    },
    options: Array,
    subfields: Object,
    field: Object,
  },
  data() {
    return {
      selected: this.value != null && this.value !== "" ? this.value : null,
    }
  },
  computed: {
    wrapperTag() {
      return this.field.horizontal ? 'div' : 'fieldset';
    },
    labelTag() {
      return this.field.horizontal ? 'label' : 'legend';
    },
    labelId() {
      return `${this.name}-label`;
    },
    labelClasses() {
      const classes = [];

      if (this.field.bold_label || this.field.description || this.field.disclaimer) {
        classes.push('bold');
      }

      return classes;
    },
    headerCompactClass() {
      if (this.field.description || this.field.disclaimer) return "radio-legend-compact";
      return "";
    },
    selectedOptionWithFields() {
      // Find the currently selected option that has nested fields
      if (this.selected !== null && this.options) {
        return this.options.find(option => option.val == this.selected && option.fields);
      }
      return null;
    }
  },
  methods: {
    wrapperClass() {
      if (this.field.horizontal) {
        return 'form-group align-items-center';
      }
      return 'radio-group-fieldset';
    },
    labelClass() {
      if (this.field.horizontal) {
        return "col-6";
      }
      return "col-12";
    },
    selectionClass() {
      if (this.field.horizontal) {
        return "col-6 padding-right-20";
      }
      return "col-12";
    },
    innerClass(optionsNum) {
      // If horizontal_options: true, make options appear horizontally (one next to the other)
      if (this.field.horizontal_options === true) {
        // Calculate column size based on number of options
        const colSize = Math.floor(12 / optionsNum);
        return `col-${colSize}`;
      }

      // Default vertical stacking behavior
      if (optionsNum <= 4 ) {
        return "col-12";
      }
      var col_size = 12 / optionsNum
      if (this.field.horizontal) {
        return "col-6";
      }
      if (this.field.wide) {
        return "col-" + col_size;
      }
      return "col-12";
    },
    nestedFieldColumnClass() {
      if (this.field.horizontal_options === true) {
        return "col-12 col-xl-6";
      }
      return "col-12";
    },
    nestedFieldsWrapperClass() {
      if (this.field.horizontal_options === true) {
        // Use col-12 to span full width and row for horizontal layout of nested fields
        return "col-12 row";
      }
      return "col-12";
    },
    customizeNestedField(field) {
      // Remove desktopClass since we're handling it with wrapper divs
      return field;
    },
    hasOptionDetail(option) {
      if (!option) return false;
      if (option.title != null && option.title !== "") return true;
      if (option.description != null && option.description !== "") return true;
      return false;
    }
  }
}
</script>
<style scoped>
.radio-group-fieldset {
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.radio-group-legend {
  border: none;
  padding: 0;
  margin: 0 0 15px;
  font: inherit;
  float: none;
  width: auto;
}

.radio-group-legend :deep(.bold) {
  font-family: 'RSBold', inherit;
}

.radio-group-legend.radio-legend-compact {
  margin-bottom: 8px;
}

.radio-field-description {
  display: block;
  margin-bottom: 8px;
}

.radio-field-disclaimer {
  display: block;
  margin-bottom: 10px;
  font-family: 'RSBold', inherit;
}

.radio-option-detail {
  display: inline-flex;
  flex-direction: column;
  vertical-align: top;
}

.radio-option-title {
  font-family: 'RSBold', inherit;
}

.radio-option-description {
  display: block;
  margin-top: 2px;
}

.padding-right-20 {
  padding-right: 27px;
}
.required {
  color: red;
  margin-left: 5px;
}
</style>
